import type { i18n as I18nInstance } from "i18next"
import type { I18nLanguage } from "@/store/settings-state"

interface BundleEntry {
	file: string
	hash: string
}

interface LocaleManifest {
	schemaVersion: number
	defaultLocale: string
	locales: string[]
	namespaces: string[]
	bundles: Record<string, Record<string, BundleEntry>>
}

const localeBaseUrl = `${import.meta.env.BASE_URL}locales/`

let manifestPromise: Promise<LocaleManifest> | undefined
const localeLoadPromises = new Map<string, Promise<void>>()

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value)

const isBundleEntry = (value: unknown): value is BundleEntry =>
	isRecord(value) && typeof value.file === "string" && typeof value.hash === "string"

const isLocaleManifest = (value: unknown): value is LocaleManifest => {
	if (
		!isRecord(value) ||
		typeof value.schemaVersion !== "number" ||
		typeof value.defaultLocale !== "string" ||
		!Array.isArray(value.locales) ||
		!value.locales.every((locale) => typeof locale === "string") ||
		!Array.isArray(value.namespaces) ||
		!value.namespaces.every((namespace) => typeof namespace === "string") ||
		!isRecord(value.bundles)
	) {
		return false
	}

	return Object.values(value.bundles).every(
		(localeBundles) =>
			isRecord(localeBundles) && Object.values(localeBundles).every(isBundleEntry),
	)
}

const fetchJson = async (url: string, cache: RequestCache): Promise<unknown> => {
	const response = await fetch(url, { cache })
	if (!response.ok) {
		throw new Error(`Failed to load ${url}: ${response.status} ${response.statusText}`)
	}

	return response.json()
}

const fetchManifest = async (): Promise<LocaleManifest> => {
	const value = await fetchJson(`${localeBaseUrl}manifest.json`, "no-cache")
	if (!isLocaleManifest(value)) {
		throw new Error("The locale manifest has an invalid format.")
	}

	return value
}

const getManifest = (): Promise<LocaleManifest> => {
	if (!manifestPromise) {
		manifestPromise = fetchManifest().catch((error: unknown) => {
			manifestPromise = undefined
			throw error
		})
	}

	return manifestPromise
}

const loadLocale = async (i18n: I18nInstance, language: I18nLanguage): Promise<void> => {
	const manifest = await getManifest()
	const localeBundles = manifest.bundles[language]
	if (!localeBundles) {
		throw new Error(`Locale "${language}" is not present in the locale manifest.`)
	}

	const resources = await Promise.all(
		manifest.namespaces.map(async (namespace) => {
			const entry = localeBundles[namespace]
			if (!entry) {
				throw new Error(`Namespace "${namespace}" is missing for locale "${language}".`)
			}

			const resource = await fetchJson(`${localeBaseUrl}${entry.file}`, "force-cache")
			if (!isRecord(resource)) {
				throw new Error(
					`Namespace "${namespace}" for locale "${language}" has an invalid format.`,
				)
			}

			return { namespace, resource }
		}),
	)

	for (const { namespace, resource } of resources) {
		i18n.addResourceBundle(language, namespace, resource, true, true)
	}
}

/**
 * Load all hashed namespaces for a non-English locale once per application session.
 * English is embedded in the application bundle and therefore requires no request.
 */
export const ensureLocaleLoaded = (i18n: I18nInstance, language: I18nLanguage): Promise<void> => {
	if (language === "en") {
		return Promise.resolve()
	}

	const existingPromise = localeLoadPromises.get(language)
	if (existingPromise) {
		return existingPromise
	}

	const loadPromise = loadLocale(i18n, language).catch((error: unknown) => {
		localeLoadPromises.delete(language)
		throw error
	})
	localeLoadPromises.set(language, loadPromise)
	return loadPromise
}

export const clearLocaleLoaderCache = (): void => {
	manifestPromise = undefined
	localeLoadPromises.clear()
}
